import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  Aangifte,
  BokkzIbDataService,
  IncomeTaxEntity,
  JaarrekeningInvesteringen,
  Onderneming
} from '@snelstart-ventures/fiscaal/domain-logic';
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {FormArray, FormControl, FormGroup} from '@angular/forms';
import {filter, Observable, shareReplay, take, tap} from 'rxjs';
import {debounceTime} from 'rxjs/operators';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiDialogModule} from '@snelstart/ui-dialog';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {CommonModule} from '@angular/common';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

type InvesteringFormGroup = FormGroup<{
  omschrijving: FormControl<string | null>;
  datumIngebruikname: FormControl<Date | null>;
  aandeelInInvesteringsBedrag: FormControl<number | null>;
  claimEIA: FormControl<boolean>;
  investeringsbedragEIA: FormControl<number | null>;
  meldingsNummerEia: FormControl<string | null>;
  claimMIA: FormControl<boolean>;
  investeringsbedragMIA: FormControl<number | null>;
  meldingsNummerMia: FormControl<string | null>;
  miaLetter: FormControl<string | null>;
  claimKIA: FormControl<boolean>;
  investeringsbedragKIA: FormControl<number | null>;
  reedsBetaaldDoorAangever: FormControl<number | null>;
  grondslagDesinvestering: FormControl<number | null>;
  percentageInvesteringsaftrek: FormControl<number | null>;
}>;

@Component({
  selector: 'snelstart-venture-fiscaal-feature-investeringen',
  templateUrl: './investeringen.component.html',
  styleUrls: ['./investeringen.component.scss'],
  imports: [
    SharedUiFormControlsModule,
    SharedUiDetailsModule,
    SharedUiDialogModule,
    SharedUiButtonModule,
    CommonModule,
    SharedUiAlertModule
  ]
})
export class InvesteringenComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  public dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  public indexOnderneming = 0;
  public heeftSamenwerkingsverband = false;
  public dialogVisible = false;
  public isOndernemer = false;
  public isPartner = false;
  public index = 0;

  public formGroup = new FormGroup({
    investeringen: new FormArray<InvesteringFormGroup>([]),
    toeTePassenInvesteringsaftrek: new FormControl<number | null>(null),
    customInput: new FormControl<boolean | null>(null),
  });

  public optionsJaNee: SelectFormControlItem<boolean>[] = [
    {value: true, label: 'Ja'},
    {value: false, label: 'Nee'}
  ];

  public optionsCustomInput: SelectFormControlItem<boolean>[] = [
    {value: true, label: 'Invullen als vast bedrag'},
    {value: false, label: 'Invullen als specificatie'}
  ];

  public miaLetter: SelectFormControlItem<string>[] = [
    {value: 'A', label: 'A = 36% MIA + 75% Vamil'},
    {value: 'B', label: 'B = 27% MIA + 75% Vamil'},
    {value: 'C', label: 'C = alleen 75% Vamil'},
    {value: 'D', label: 'D = alleen 36% MIA'},
    {value: 'E', label: 'E = alleen 27% MIA'},
    {value: 'F', label: 'F = 45% MIA + 75% Vamil'},
    {value: 'G', label: 'G = alleen 45% MIA'}];

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly indexOnderneming$: Observable<number> =
    this.dataService.indexOnderneming$.pipe(
      tap(data => {
        this.indexOnderneming = data;
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  ngOnInit(): void {
    this.indexOnderneming$.subscribe();
    this.dataService.partnerAangifte$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(partner => {
      this.isPartner = partner;
      this.incomeTaxEntity$.pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(entity => {
        if (partner) {
          this.handleSwitch(entity, true);
        } else {
          this.handleSwitch(entity, false);
        }
      });
    });

    // Subscribe to changes in FormArrays
    this.investeringen.valueChanges
      .pipe(
        debounceTime(500),
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.saveForm())
      ).subscribe();
  }

  ngOnDestroy(): void {
    this.dataService.calculateLatestIncomeTaxReturn();
  }

  private handleSwitch(entity: IncomeTaxEntity, isPartner: boolean): void {
    const details = isPartner ? entity.aangifte?.partnerAangifte : entity.aangifte;
    if (details) {
      this.resetAndPatchForms(details);
    }
  }

  private resetAndPatchForms(details: Aangifte): void {
    this.formGroup.reset({}, {emitEvent: false});
    this.isOndernemer = false;
    if (details.box1?.winstUitOnderneming?.ondernemingen?.at(this.indexOnderneming)) {
      this.isOndernemer = true;
      this.updateForm(details.box1?.winstUitOnderneming?.ondernemingen?.at(this.indexOnderneming)?.investeringen || []);
      this.formGroup.patchValue({toeTePassenInvesteringsaftrek: details.box1?.winstUitOnderneming?.ondernemingen?.at(this.indexOnderneming)?.toeTePassenInvesteringsaftrek}, {emitEvent : false});
      if (details.box1?.winstUitOnderneming?.ondernemingen?.at(this.indexOnderneming)?.toeTePassenInvesteringsaftrek !== undefined) {
        this.formGroup.patchValue({customInput: true})
      }
      if (details.box1?.winstUitOnderneming?.ondernemingen?.at(this.indexOnderneming)?.investeringen?.length ?? 0 > 0) {
        this.formGroup.patchValue({customInput: false})
      }
    }
  }

  private setupDesinvesteringReset(group: FormGroup) {
    const claimKIA = group.get('claimKIA');
    const claimEIA = group.get('claimEIA');
    const claimMIA = group.get('claimMIA');
    const grondslag = group.get('grondslagDesinvestering');
    const percentage = group.get('percentageInvesteringsaftrek');
    if (!claimKIA || !claimEIA || !claimMIA || !grondslag) return;

    claimKIA.valueChanges.subscribe((kia: boolean) => {
      if (kia) {
        grondslag.setValue(null, { emitEvent: false });
        if (percentage) {
          percentage.setValue(null, { emitEvent: false });
        }
      }
    });
    claimEIA.valueChanges.subscribe((eia: boolean) => {
      if (eia) {
        grondslag.setValue(null, { emitEvent: false });
        if (percentage) {
          percentage.setValue(null, { emitEvent: false });
        }
        if (claimMIA) {
          claimMIA.setValue(false, { emitEvent: false });
        }
      }
    });
    claimMIA.valueChanges.subscribe((mia: boolean) => {
      if (mia) {
        grondslag.setValue(null, { emitEvent: false });
        if (percentage) {
          percentage.setValue(null, { emitEvent: false });
        }
        if (claimEIA) {
          claimEIA.setValue(false, { emitEvent: false });
        }
      }
    });
    grondslag.valueChanges.subscribe((gr: number) => {
      if (gr) {
        if (claimKIA) {
          claimKIA.setValue(false, { emitEvent: false });
        }
        if (claimEIA) {
          claimEIA.setValue(false, { emitEvent: false });
        }
        if (claimMIA) {
          claimMIA.setValue(false, { emitEvent: false });
        }
      }
      else {
        if (percentage) {
          percentage.setValue(null, { emitEvent: false });
        }
      }
    });
  }

  showDialog(index: number) {
    this.index = index;
    this.dialogVisible = true;
  }

  get investeringen(): FormArray<InvesteringFormGroup> {
    return this.formGroup.controls.investeringen;
  }

  addRij(): void {
    const group = this.createFormGroup();
    this.setupDesinvesteringReset(group);
    this.investeringen.push(group);
  }

  verwijderRij(index: number): void {
    this.investeringen.removeAt(index, { emitEvent: false });
    this.formGroup.updateValueAndValidity({ emitEvent: false });
    this.formGroup.markAsDirty({emitEvent: false});
    this.saveForm();
  }

  createFormGroup(item?: JaarrekeningInvesteringen): FormGroup {
    return new FormGroup({
      omschrijving: new FormControl<string | null>(item?.omschrijving ?? null),
      datumIngebruikname: new FormControl<Date | null>(item?.datumIngebruikname ?? null),
      aandeelInInvesteringsBedrag: new FormControl<number | null>(item?.aandeelInInvesteringsBedrag ?? null),
      claimEIA: new FormControl<boolean>(item?.claimEIA ?? false),
      investeringsbedragEIA: new FormControl<number | null>(item?.investeringsbedragEIA ?? null),
      meldingsNummerEia: new FormControl<string | null>(item?.meldingsNummerEia ?? null),
      claimMIA: new FormControl<boolean>(item?.claimMIA ?? false),
      investeringsbedragMIA: new FormControl<number | null>(item?.investeringsbedragMIA ?? null),
      meldingsNummerMia: new FormControl<string | null>(item?.meldingsNummerMia ?? null),
      miaLetter: new FormControl<string | null>(item?.miaLetter ?? null),
      claimKIA: new FormControl<boolean>(item?.claimKIA ?? false),
      investeringsbedragKIA: new FormControl<number | null>(item?.investeringsbedragKIA ?? null),
      reedsBetaaldDoorAangever: new FormControl<number | null>(item?.reedsBetaaldDoorAangever ?? null),
      grondslagDesinvestering: new FormControl<number | null>(item?.grondslagDesinvestering ?? null),
      percentageInvesteringsaftrek: new FormControl<number | null>(item?.percentageInvesteringsaftrek ?? null),
    })
  }

  private updateForm(form: JaarrekeningInvesteringen[]): void {
    let lijst = form || [];
    const formArray = this.investeringen;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        control.patchValue(lijst[i], {emitEvent: false});
        this.setupDesinvesteringReset(control);
      } else {
        const formGroup = this.createFormGroup(lijst[i]);
        this.setupDesinvesteringReset(formGroup);
        formArray.push(formGroup, {emitEvent: false});
      }
    }
  }

  saveForm(): void {
    const idx = this.indexOnderneming;
    const formValue = this.formGroup.getRawValue();

    this.dataService.patchIncomeTaxEntity((currentEntity) => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }
      const update = (aangifte: any) => {
        const existingOndernemingen = aangifte.box1?.winstUitOnderneming?.ondernemingen || [];
        const onderneming = existingOndernemingen[idx] || {};
        const updatedOnderneming: Partial<Onderneming> = {
          ...onderneming,
          investeringen: formValue.investeringen,
          toeTePassenInvesteringsaftrek: formValue.investeringen.length > 0 ? undefined : formValue.toeTePassenInvesteringsaftrek ?? undefined,
        };
        // Clean all empty arrays, arrays of empty objects, and empty objects from the outgoing data
        const cleanedOnderneming = this.dataService.deepClean(updatedOnderneming);
        const updatedOndernemingen = existingOndernemingen.map((o: Onderneming, i: number) => i === idx ? cleanedOnderneming : o);
        return {
          ...aangifte,
          box1: {
            ...aangifte.box1,
            winstUitOnderneming: {
              ...aangifte.box1?.winstUitOnderneming,
              ondernemingen: updatedOndernemingen,
            },
          },
        };
      };
      const updatedAangifte = this.isPartner
        ? {
          ...currentEntity.aangifte,
          partnerAangifte: update(currentEntity.aangifte?.partnerAangifte!),
        }
        : update(currentEntity.aangifte);
      return this.dataService.deepClean({
        ...currentEntity,
        aangifte: updatedAangifte,
      }) as IncomeTaxEntity;
    });
  }
}
